import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/firestore';
import { collection, doc, getDocs, query, orderBy, addDoc, deleteDoc, updateDoc } from "firebase/firestore";
import { 
  Plus, Save, Trash2, Upload, X, ChevronUp, ChevronDown, GripVertical,
  Bold, Italic, Underline, List, Link as LinkIcon, Music, BookOpen, Video, ExternalLink, GraduationCap,
  Image as ImageIcon, Eye, EyeOff, FileText, Edit, Play, FolderOpen, Library
} from 'lucide-react';

// Типы контента для блоков
const CONTENT_TYPES = {
  VIDEO: 'video',
  AUDIO: 'audio',
  LESSON: 'lesson',
  TRAINING: 'training',
  BUTTON: 'button',
  TEXT: 'text',
  COURSE_AUDIO: 'course_audio' // Новый тип для аудио, загруженных в курс
};

const BASE_URL = "https://artdownload2-production.up.railway.app";

export default function CourseBuilder() {
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  
  // Списки контента из БД
  const [globalAudioLessons, setGlobalAudioLessons] = useState([]); // Глобальная библиотека аудиоуроков
  const [masterClasses, setMasterClasses] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [showContentSelector, setShowContentSelector] = useState({ 
    blockIndex: null, 
    contentType: null 
  });
  
  // Состояние для управления глобальными аудиоуроками
  const [showGlobalAudioManager, setShowGlobalAudioManager] = useState(false);
  const [editingGlobalAudio, setEditingGlobalAudio] = useState(null);
  const [globalAudioFormData, setGlobalAudioFormData] = useState({
    title: '',
    description: '',
    audioUrl: '',
    image: '',
    duration: '',
    premium: false
  });
  
  // Состояние для загрузки аудио в текущий курс
  const [showCourseAudioUpload, setShowCourseAudioUpload] = useState(false);
  const [uploadingCourseAudio, setUploadingCourseAudio] = useState(false);
  const [courseAudioUploadProgress, setCourseAudioUploadProgress] = useState(0);
  
  const fileInputRef = useRef(null);
  const blockImageInputRef = useRef(null);
  const audioFileInputRef = useRef(null);
  const audioImageInputRef = useRef(null);
  const courseAudioFileInputRef = useRef(null);

  // Загрузка всех курсов и контента
  useEffect(() => {
    loadCourses();
    loadContentLists();
  }, []);

  const loadContentLists = async () => {
    try {
      // Загрузка глобальных аудио-уроков
      const audioRef = collection(db, 'audioLessons');
      const audioSnapshot = await getDocs(audioRef);
      setGlobalAudioLessons(audioSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Загрузка мастер-классов (уроков)
      const lessonsRef = collection(db, 'masterClasses');
      const lessonsSnapshot = await getDocs(lessonsRef);
      setMasterClasses(lessonsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Загрузка тренингов
      const trainingsRef = collection(db, 'trainings');
      const trainingsSnapshot = await getDocs(trainingsRef);
      setTrainings(trainingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    } catch (error) {
      console.error('Error loading content lists:', error);
    }
  };

  const loadCourses = async () => {
    setLoading(true);
    try {
      const coursesRef = collection(db, 'courses');
      const q = query(coursesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const coursesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        courseAudioLessons: doc.data().courseAudioLessons || [] // Собственные аудиоуроки курса
      }));
      setCourses(coursesData);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
    setLoading(false);
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    
    if (!file.type.startsWith('image/')) {
      alert('Пожалуйста, выберите изображение');
      return null;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      alert('Размер файла не должен превышать 10MB');
      return null;
    }

    setUploading(true);
    setUploadError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BASE_URL}/upload?folder=course-images`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Ошибка загрузки');

      const data = await response.json();
      
      if (data.success) {
        return `${BASE_URL}${data.url}`;
      } else {
        throw new Error('Ошибка при загрузке');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError(error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Загрузка аудио для глобальной библиотеки
  const uploadAudioFile = async (file) => {
    if (!file) return null;
    
    if (!file.type.startsWith('audio/')) {
      alert('Пожалуйста, выберите аудио файл (MP3, WAV, AAC)');
      return null;
    }
    
    if (file.size > 100 * 1024 * 1024) {
      alert('Размер файла не должен превышать 100MB');
      return null;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BASE_URL}/upload?folder=audio`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Ошибка загрузки');

      const data = await response.json();
      
      if (data.success) {
        return `${BASE_URL}${data.url}`;
      } else {
        throw new Error('Ошибка при загрузке');
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
      setUploadError(error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Загрузка аудио в текущий курс
  const uploadCourseAudio = async (file) => {
    if (!file) return null;
    
    if (!file.type.startsWith('audio/')) {
      alert('Пожалуйста, выберите аудио файл (MP3, WAV, AAC)');
      return null;
    }

    setUploadingCourseAudio(true);
    setCourseAudioUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      const promise = new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = (e.loaded / e.total) * 100;
            setCourseAudioUploadProgress(percent);
          }
        });
        
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error('Ошибка загрузки'));
          }
        };
        
        xhr.onerror = () => reject(new Error('Ошибка сети'));
      });
      
      xhr.open('POST', `${BASE_URL}/upload?folder=course-audio`);
      xhr.send(formData);
      
      const data = await promise;
      
      if (data.success) {
        return `${BASE_URL}${data.url}`;
      } else {
        throw new Error('Ошибка при загрузке');
      }
    } catch (error) {
      console.error('Error uploading course audio:', error);
      setUploadError(error.message);
      return null;
    } finally {
      setUploadingCourseAudio(false);
      setCourseAudioUploadProgress(0);
    }
  };

  // Функции для управления глобальными аудиоуроками
  const saveGlobalAudioLesson = async () => {
    if (!globalAudioFormData.title.trim()) {
      alert('Введите название аудиоурока');
      return;
    }
    
    if (!globalAudioFormData.audioUrl) {
      alert('Загрузите аудио файл');
      return;
    }
    
    try {
      const audioData = {
        title: globalAudioFormData.title,
        description: globalAudioFormData.description,
        audioUrl: globalAudioFormData.audioUrl,
        image: globalAudioFormData.image || '',
        duration: globalAudioFormData.duration || '',
        premium: globalAudioFormData.premium,
        updatedAt: new Date().toISOString()
      };
      
      if (editingGlobalAudio) {
        await updateDoc(doc(db, 'audioLessons', editingGlobalAudio.id), audioData);
        alert('Аудиоурок обновлен успешно!');
      } else {
        audioData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'audioLessons'), audioData);
        alert('Аудиоурок создан успешно!');
      }
      
      resetGlobalAudioForm();
      await loadContentLists();
      setShowGlobalAudioManager(false);
      
    } catch (error) {
      console.error('Error saving global audio lesson:', error);
      alert('Ошибка при сохранении аудиоурока');
    }
  };

  const deleteGlobalAudioLesson = async (audioId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот аудиоурок из глобальной библиотеки?')) return;
    
    try {
      await deleteDoc(doc(db, 'audioLessons', audioId));
      await loadContentLists();
      alert('Аудиоурок удален успешно!');
    } catch (error) {
      console.error('Error deleting global audio lesson:', error);
      alert('Ошибка при удалении аудиоурока');
    }
  };

  const editGlobalAudioLesson = (audio) => {
    setEditingGlobalAudio(audio);
    setGlobalAudioFormData({
      title: audio.title || '',
      description: audio.description || '',
      audioUrl: audio.audioUrl || '',
      image: audio.image || '',
      duration: audio.duration || '',
      premium: audio.premium || false
    });
    setShowGlobalAudioManager(true);
  };

  const resetGlobalAudioForm = () => {
    setEditingGlobalAudio(null);
    setGlobalAudioFormData({
      title: '',
      description: '',
      audioUrl: '',
      image: '',
      duration: '',
      premium: false
    });
  };

  const handleGlobalAudioFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const audioUrl = await uploadAudioFile(file);
    if (audioUrl) {
      setGlobalAudioFormData(prev => ({ ...prev, audioUrl }));
      // Автоматическое определение длительности
      const audio = new Audio(audioUrl);
      audio.addEventListener('loadedmetadata', () => {
        const duration = Math.floor(audio.duration);
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        setGlobalAudioFormData(prev => ({ 
          ...prev, 
          duration: `${minutes}:${seconds.toString().padStart(2, '0')}` 
        }));
      });
    }
    e.target.value = '';
  };

  const handleGlobalAudioImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const imageUrl = await uploadImage(file);
    if (imageUrl) {
      setGlobalAudioFormData(prev => ({ ...prev, image: imageUrl }));
    }
    e.target.value = '';
  };

  // Функции для загрузки аудио в курс
  const handleCourseAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const audioUrl = await uploadCourseAudio(file);
    if (audioUrl && currentCourse) {
      const newCourseAudio = {
        id: Date.now().toString(),
        title: file.name.replace(/\.[^/.]+$/, ""),
        fileName: file.name,
        audioUrl: audioUrl,
        duration: '',
        uploadedAt: new Date().toISOString()
      };
      
      // Автоматическое определение длительности
      const audio = new Audio(audioUrl);
      audio.addEventListener('loadedmetadata', () => {
        const duration = Math.floor(audio.duration);
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        newCourseAudio.duration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        
        const updatedCourse = {
          ...currentCourse,
          courseAudioLessons: [...(currentCourse.courseAudioLessons || []), newCourseAudio]
        };
        setCurrentCourse(updatedCourse);
      });
      
      // Если не удалось определить длительность, добавляем без нее
      if (!newCourseAudio.duration) {
        const updatedCourse = {
          ...currentCourse,
          courseAudioLessons: [...(currentCourse.courseAudioLessons || []), newCourseAudio]
        };
        setCurrentCourse(updatedCourse);
      }
    }
    e.target.value = '';
  };

  const deleteCourseAudio = (audioId) => {
    if (!window.confirm('Удалить этот аудиофайл из курса?')) return;
    
    const updatedCourse = {
      ...currentCourse,
      courseAudioLessons: currentCourse.courseAudioLessons.filter(audio => audio.id !== audioId)
    };
    setCurrentCourse(updatedCourse);
  };

  const createNewCourse = async () => {
    const newCourse = {
      title: 'Новый курс',
      description: '',
      image: '',
      order: courses.length,
      status: 'draft',
      premium: false,
      paidAccessId: '',
      blocks: [],
      courseAudioLessons: [], // Собственные аудиоуроки курса
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      const docRef = await addDoc(collection(db, 'courses'), newCourse);
      setSelectedCourseId(docRef.id);
      setCurrentCourse({ id: docRef.id, ...newCourse });
      loadCourses();
    } catch (error) {
      console.error('Error creating course:', error);
    }
  };

const saveCourse = async () => {
  if (!currentCourse) return;
  
  if (!currentCourse.title?.trim()) {
    alert('Введите название курса');
    return;
  }
  
  try {
    const courseRef = doc(db, 'courses', currentCourse.id);
    
    // Функция для очистки объекта от undefined значений
    const removeUndefined = (obj) => {
      if (obj === null || typeof obj !== 'object') return obj;
      
      if (Array.isArray(obj)) {
        return obj.map(item => removeUndefined(item));
      }
      
      const cleaned = {};
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (value !== undefined) {
          cleaned[key] = removeUndefined(value);
        }
      });
      return cleaned;
    };
    
    // Очищаем данные от undefined
    const cleanedData = removeUndefined({
      ...currentCourse,
      updatedAt: new Date().toISOString()
    });
    
    await updateDoc(courseRef, cleanedData);
    loadCourses();
    alert('Курс сохранен успешно!');
  } catch (error) {
    console.error('Error saving course:', error);
    alert('Ошибка при сохранении: ' + error.message);
  }
};

  const deleteCourse = async () => {
    if (!currentCourse) return;
    
    if (window.confirm('Вы уверены, что хотите удалить этот курс?')) {
      try {
        await deleteDoc(doc(db, 'courses', currentCourse.id));
        
        const updatedCourses = courses
          .filter(c => c.id !== currentCourse.id)
          .sort((a, b) => a.order - b.order)
          .map((course, index) => {
            updateDoc(doc(db, 'courses', course.id), { order: index });
            return { ...course, order: index };
          });
        
        setCurrentCourse(null);
        setSelectedCourseId(null);
        loadCourses();
      } catch (error) {
        console.error('Error deleting course:', error);
      }
    }
  };

const toggleCourseStatus = async () => {
  if (!currentCourse) return;
  
  const newStatus = currentCourse.status === 'draft' ? 'published' : 'draft';
  const action = newStatus === 'published' ? 'опубликовать' : 'перевести в черновик';
  
  if (window.confirm(`Вы уверены, что хотите ${action} этот курс?`)) {
    try {
      const courseRef = doc(db, 'courses', currentCourse.id);
      const updateData = {
        status: newStatus,
        updatedAt: new Date().toISOString()
      };
      
      // При публикации НЕ сбрасываем настройки доступа
      // Оставляем premium, paidAccessId, isGift как есть
      
      // Функция для удаления undefined из объекта
      const removeUndefined = (obj) => {
        if (obj === null || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(item => removeUndefined(item));
        
        const cleaned = {};
        Object.keys(obj).forEach(key => {
          const value = obj[key];
          if (value !== undefined) {
            cleaned[key] = removeUndefined(value);
          }
        });
        return cleaned;
      };
      
      const cleanedData = removeUndefined(updateData);
      await updateDoc(courseRef, cleanedData);
      
      // Обновляем локальное состояние
      const updatedCourse = {
        ...currentCourse,
        status: newStatus
      };
      
      setCurrentCourse(updatedCourse);
      loadCourses();
      alert(`Курс ${newStatus === 'published' ? 'опубликован' : 'переведен в черновик'}`);
    } catch (error) {
      console.error('Error toggling course status:', error);
      alert('Ошибка при изменении статуса: ' + error.message);
    }
  }
};

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadProgress(0);
    const imageUrl = await uploadImage(file);
    
    if (imageUrl) {
      setCurrentCourse({
        ...currentCourse,
        image: imageUrl
      });
      setUploadError(null);
    }
    
    e.target.value = '';
  };

  const handleBlockImageUpload = async (e, blockIndex) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadProgress(0);
    const imageUrl = await uploadImage(file);
    
    if (imageUrl) {
      updateBlock(blockIndex, 'image', imageUrl);
      setUploadError(null);
    }
    
    e.target.value = '';
  };

  // Функции для работы с блоками
  const addBlock = () => {
    if (!currentCourse) return;
    
    const newBlock = {
      id: Date.now() + Math.random(),
      title: `Блок ${(currentCourse.blocks?.length || 0) + 1}`,
      description: 'Описание блока',
      image: '',
      contents: []
    };
    
    setCurrentCourse({
      ...currentCourse,
      blocks: [...(currentCourse.blocks || []), newBlock]
    });
  };

  const removeBlock = (blockIndex) => {
    if (!currentCourse || !currentCourse.blocks || currentCourse.blocks.length <= 1) return;
    
    if (window.confirm('Удалить этот блок?')) {
      const newBlocks = currentCourse.blocks.filter((_, i) => i !== blockIndex);
      setCurrentCourse({
        ...currentCourse,
        blocks: newBlocks
      });
    }
  };

  const moveBlock = (blockIndex, direction) => {
    const newBlocks = [...currentCourse.blocks];
    if (direction === 'up' && blockIndex > 0) {
      [newBlocks[blockIndex - 1], newBlocks[blockIndex]] = [newBlocks[blockIndex], newBlocks[blockIndex - 1]];
    } else if (direction === 'down' && blockIndex < newBlocks.length - 1) {
      [newBlocks[blockIndex], newBlocks[blockIndex + 1]] = [newBlocks[blockIndex + 1], newBlocks[blockIndex]];
    } else {
      return;
    }
    
    setCurrentCourse({
      ...currentCourse,
      blocks: newBlocks
    });
  };

  const updateBlock = (blockIndex, field, value) => {
    const newBlocks = [...currentCourse.blocks];
    newBlocks[blockIndex] = {
      ...newBlocks[blockIndex],
      [field]: value
    };
    setCurrentCourse({
      ...currentCourse,
      blocks: newBlocks
    });
  };

  // Функции для работы с контентом в блоках
  const addContentToBlock = (blockIndex, contentType, contentData) => {
    const newBlocks = [...currentCourse.blocks];
    if (!newBlocks[blockIndex].contents) {
      newBlocks[blockIndex].contents = [];
    }
    
    newBlocks[blockIndex].contents.push({
      id: Date.now() + Math.random(),
      type: contentType,
      data: contentData
    });
    
    setCurrentCourse({
      ...currentCourse,
      blocks: newBlocks
    });
    setShowContentSelector({ blockIndex: null, contentType: null });
  };

  const removeContentFromBlock = (blockIndex, contentIndex) => {
    const newBlocks = [...currentCourse.blocks];
    newBlocks[blockIndex].contents = newBlocks[blockIndex].contents.filter((_, i) => i !== contentIndex);
    
    setCurrentCourse({
      ...currentCourse,
      blocks: newBlocks
    });
  };

  const moveContentInBlock = (blockIndex, contentIndex, direction) => {
    const newBlocks = [...currentCourse.blocks];
    const contents = [...newBlocks[blockIndex].contents];
    
    if (direction === 'up' && contentIndex > 0) {
      [contents[contentIndex - 1], contents[contentIndex]] = [contents[contentIndex], contents[contentIndex - 1]];
    } else if (direction === 'down' && contentIndex < contents.length - 1) {
      [contents[contentIndex], contents[contentIndex + 1]] = [contents[contentIndex + 1], contents[contentIndex]];
    }
    
    newBlocks[blockIndex].contents = contents;
    setCurrentCourse({
      ...currentCourse,
      blocks: newBlocks
    });
  };

  // Функции для форматирования описания
  const insertFormatting = (type) => {
    const textarea = document.getElementById('course-description');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);
    
    let formattedText = '';
    switch(type) {
      case 'bold':
        formattedText = `**${selectedText || 'жирный текст'}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText || 'курсив'}*`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText || 'подчеркнутый текст'}</u>`;
        break;
      case 'list':
        formattedText = selectedText
          .split('\n')
          .map(line => `• ${line}`)
          .join('\n');
        break;
      case 'link':
        const url = prompt('Введите URL ссылки:', 'https://');
        if (url) {
          formattedText = `[${selectedText || 'ссылка'}](${url})`;
        } else {
          return;
        }
        break;
      default:
        return;
    }
    
    const newText = beforeText + formattedText + afterText;
    setCurrentCourse({
      ...currentCourse,
      description: newText
    });
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(beforeText.length + formattedText.length, beforeText.length + formattedText.length);
    }, 0);
  };

  const moveCourse = async (direction) => {
    if (!currentCourse) return;
    
    const currentIndex = courses.findIndex(c => c.id === currentCourse.id);
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === courses.length - 1) return;
    
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const targetCourse = courses[targetIndex];
    
    await Promise.all([
      updateDoc(doc(db, 'courses', currentCourse.id), { order: targetIndex }),
      updateDoc(doc(db, 'courses', targetCourse.id), { order: currentIndex })
    ]);
    
    loadCourses();
    setCurrentCourse({
      ...currentCourse,
      order: targetIndex
    });
  };

  // Получение статуса курса для отображения
const getCourseStatus = (course) => {
  if (course.status === 'draft') {
    return { 
      label: 'Черновик', 
      color: 'text-gray-400', 
      bg: 'bg-gray-700/50', 
      icon: <FileText className="w-3 h-3" /> 
    };
  } else {
    return { 
      label: 'Опубликован', 
      color: 'text-green-400', 
      bg: 'bg-green-900/30', 
      icon: <Eye className="w-3 h-3" /> 
    };
  }
};

  // Рендер менеджера глобальных аудиоуроков
  const renderGlobalAudioManager = () => {
    if (!showGlobalAudioManager) return null;
    
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
          <div className="sticky top-0 bg-gray-900 p-4 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Library className="w-5 h-5 text-green-400" />
              {editingGlobalAudio ? 'Редактировать аудиоурок' : 'Создать аудиоурок в библиотеку'}
            </h3>
            <button
              onClick={() => {
                setShowGlobalAudioManager(false);
                resetGlobalAudioForm();
              }}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Название */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Название аудиоурока *
              </label>
              <input
                type="text"
                value={globalAudioFormData.title}
                onChange={(e) => setGlobalAudioFormData({ ...globalAudioFormData, title: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                placeholder="Введите название"
              />
            </div>

            {/* Аудио файл */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Аудио файл *
              </label>
              {globalAudioFormData.audioUrl ? (
                <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                  <Music className="w-5 h-5 text-green-400" />
                  <div className="flex-1">
                    <audio controls className="w-full">
                      <source src={globalAudioFormData.audioUrl} />
                    </audio>
                  </div>
                  <button
                    onClick={() => setGlobalAudioFormData({ ...globalAudioFormData, audioUrl: '' })}
                    className="p-1 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    ref={audioFileInputRef}
                    accept="audio/*"
                    onChange={handleGlobalAudioFileUpload}
                    className="hidden"
                    id="global-audio-file-upload"
                  />
                  <label
                    htmlFor="global-audio-file-upload"
                    className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-green-500 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-gray-500 mb-2" />
                    <p className="text-sm text-gray-400">Нажмите для загрузки аудио</p>
                    <p className="text-xs text-gray-500 mt-1">MP3, WAV, AAC (до 100MB)</p>
                  </label>
                </>
              )}
            </div>

            {/* Изображение */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Обложка
              </label>
              <div className="flex items-center gap-3">
                {globalAudioFormData.image && (
                  <div className="w-20 h-20 rounded-lg overflow-hidden border border-gray-700">
                    <img src={globalAudioFormData.image} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    ref={audioImageInputRef}
                    accept="image/*"
                    onChange={handleGlobalAudioImageUpload}
                    className="hidden"
                    id="global-audio-image-upload"
                  />
                  <label
                    htmlFor="global-audio-image-upload"
                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 text-sm"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Загрузить обложку
                  </label>
                  {globalAudioFormData.image && (
                    <button
                      onClick={() => setGlobalAudioFormData({ ...globalAudioFormData, image: '' })}
                      className="ml-2 p-2 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Длительность */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Длительность
              </label>
              <input
                type="text"
                value={globalAudioFormData.duration}
                onChange={(e) => setGlobalAudioFormData({ ...globalAudioFormData, duration: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                placeholder="Например: 15:30"
              />
            </div>

            {/* Описание */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Описание
              </label>
              <textarea
                value={globalAudioFormData.description}
                onChange={(e) => setGlobalAudioFormData({ ...globalAudioFormData, description: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                rows="4"
                placeholder="Описание аудиоурока..."
              />
            </div>

            {/* Премиум */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="global-audio-premium"
                checked={globalAudioFormData.premium}
                onChange={(e) => setGlobalAudioFormData({ ...globalAudioFormData, premium: e.target.checked })}
                className="w-4 h-4 text-green-500 bg-gray-800 border-gray-700 rounded"
              />
              <label htmlFor="global-audio-premium" className="ml-2 text-sm text-gray-300">
                Премиум доступ
              </label>
            </div>

            {/* Кнопки */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowGlobalAudioManager(false);
                  resetGlobalAudioForm();
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={saveGlobalAudioLesson}
                disabled={uploading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {editingGlobalAudio ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Рендер менеджера аудиофайлов курса
  const renderCourseAudioManager = () => {
    if (!showCourseAudioUpload) return null;
    
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-gray-700">
          <div className="sticky top-0 bg-gray-900 p-4 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Music className="w-5 h-5 text-green-400" />
              Аудиофайлы курса
            </h3>
            <button
              onClick={() => setShowCourseAudioUpload(false)}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4">
            {/* Загрузка нового аудио */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Загрузить аудио в курс
              </label>
              <input
                type="file"
                ref={courseAudioFileInputRef}
                accept="audio/*"
                onChange={handleCourseAudioUpload}
                className="hidden"
                id="course-audio-upload"
              />
              <label
                htmlFor="course-audio-upload"
                className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-green-500 transition-colors"
              >
                <Upload className="w-8 h-8 text-gray-500 mb-2" />
                <p className="text-sm text-gray-400">Нажмите для загрузки аудио</p>
                <p className="text-xs text-gray-500 mt-1">MP3, WAV, AAC</p>
              </label>
              {uploadingCourseAudio && (
                <div className="mt-2">
                  <div className="text-sm text-gray-400 mb-1">Загрузка: {courseAudioUploadProgress.toFixed(0)}%</div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${courseAudioUploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Список аудиофайлов курса */}
            {currentCourse?.courseAudioLessons && currentCourse.courseAudioLessons.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Аудиофайлы курса ({currentCourse.courseAudioLessons.length})
                </label>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {currentCourse.courseAudioLessons.map((audio) => (
                    <div key={audio.id} className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                      <Music className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white truncate">{audio.title}</div>
                        <div className="text-xs text-gray-400">
                          {audio.duration && `🕐 ${audio.duration}`}
                          {audio.fileName && ` • ${audio.fileName}`}
                        </div>
                        <audio controls className="w-full mt-2">
                          <source src={audio.audioUrl} />
                        </audio>
                      </div>
                      <button
                        onClick={() => deleteCourseAudio(audio.id)}
                        className="p-2 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Рендер выбора контента
  const renderContentSelector = (blockIndex) => {
    const { contentType } = showContentSelector;
    if (!contentType) return null;

    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-gray-700">
          <div className="sticky top-0 bg-gray-900 p-4 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">
              {contentType === CONTENT_TYPES.VIDEO && '📹 Добавить видео'}
              {contentType === CONTENT_TYPES.AUDIO && '🎵 Добавить аудио из библиотеки'}
              {contentType === CONTENT_TYPES.COURSE_AUDIO && '🎵 Добавить аудио курса'}
              {contentType === CONTENT_TYPES.LESSON && '📚 Добавить урок'}
              {contentType === CONTENT_TYPES.TRAINING && '🎓 Добавить тренинг'}
              {contentType === CONTENT_TYPES.BUTTON && '🔗 Добавить кнопку'}
              {contentType === CONTENT_TYPES.TEXT && '📝 Добавить текст'}
            </h3>
            <button
              onClick={() => setShowContentSelector({ blockIndex: null, contentType: null })}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4">
            {/* Видео */}
            {contentType === CONTENT_TYPES.VIDEO && (
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="ID видео из Kinescope"
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                  id="kinescopeInput"
                />
                <button
                  onClick={() => {
                    const input = document.getElementById('kinescopeInput');
                    if (input.value) {
                      addContentToBlock(blockIndex, CONTENT_TYPES.VIDEO, { kinescopeId: input.value });
                    }
                  }}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                >
                  Добавить видео
                </button>
              </div>
            )}

            {/* Аудио из глобальной библиотеки */}
            {contentType === CONTENT_TYPES.AUDIO && (
              <div className="space-y-3">
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => {
                      setShowContentSelector({ blockIndex: null, contentType: null });
                      setShowGlobalAudioManager(true);
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Создать в библиотеке
                  </button>
                </div>
                
                <div className="border-t border-gray-700 my-3"></div>
                
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {globalAudioLessons.map(item => (
                    <button
                      key={item.id}
                      onClick={() => addContentToBlock(blockIndex, CONTENT_TYPES.AUDIO, { 
                        id: item.id,
                        title: item.title,
                        description: item.description,
                        audioUrl: item.audioUrl,
                        image: item.image,
                        duration: item.duration,
                        premium: item.premium,
                        type: 'global',
                        path: `/audioLessons/${item.id}`
                      })}
                      className="w-full p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-left transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        {item.image ? (
                          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={item.image} alt="" className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Music className="w-5 h-5 text-green-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-white truncate">{item.title || 'Без названия'}</div>
                          {item.description && (
                            <div className="text-xs text-gray-400 truncate mt-0.5">{item.description}</div>
                          )}
                          <div className="flex gap-2 mt-1">
                            {item.duration && (
                              <span className="text-xs text-gray-500">🕐 {item.duration}</span>
                            )}
                            {item.premium && (
                              <span className="text-xs text-yellow-500">⭐ Премиум</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              editGlobalAudioLesson(item);
                            }}
                            className="p-1 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded"
                          >
                            <Edit className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteGlobalAudioLesson(item.id);
                            }}
                            className="p-1 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </button>
                  ))}
                  {globalAudioLessons.length === 0 && (
                    <div className="text-gray-500 text-center p-4 text-sm">
                      Нет аудиоуроков в библиотеке. Нажмите "Создать в библиотеке"
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Аудио курса */}
            {contentType === CONTENT_TYPES.COURSE_AUDIO && (
              <div className="space-y-3">
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => {
                      setShowContentSelector({ blockIndex: null, contentType: null });
                      setShowCourseAudioUpload(true);
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center justify-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Загрузить аудио в курс
                  </button>
                </div>
                
                <div className="border-t border-gray-700 my-3"></div>
                
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {currentCourse?.courseAudioLessons && currentCourse.courseAudioLessons.length > 0 ? (
                    currentCourse.courseAudioLessons.map(item => (
                      <button
                        key={item.id}
                        onClick={() => addContentToBlock(blockIndex, CONTENT_TYPES.COURSE_AUDIO, { 
                          id: item.id,
                          title: item.title,
                          fileName: item.fileName,
                          audioUrl: item.audioUrl,
                          duration: item.duration,
                          type: 'course',
                          courseId: currentCourse.id
                        })}
                        className="w-full p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-left transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Music className="w-5 h-5 text-green-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-white truncate">{item.title}</div>
                            <div className="text-xs text-gray-400">
                              {item.duration && `🕐 ${item.duration}`}
                              {item.fileName && ` • ${item.fileName}`}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="text-gray-500 text-center p-4 text-sm">
                      Нет аудиофайлов в курсе. Нажмите "Загрузить аудио в курс"
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Уроки */}
            {contentType === CONTENT_TYPES.LESSON && (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {masterClasses.map(item => (
                  <button
                    key={item.id}
                    onClick={() => addContentToBlock(blockIndex, CONTENT_TYPES.LESSON, { 
                      id: item.id,
                      title: item.title,
                      path: `/masterClasses/${item.id}`
                    })}
                    className="w-full p-2 bg-gray-800 hover:bg-gray-700 rounded text-left transition-colors flex items-center gap-2 text-sm"
                  >
                    <BookOpen className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span className="text-white truncate">{item.title || 'Без названия'}</span>
                    <span className="text-gray-500 text-xs ml-auto">{item.duration || '—'}</span>
                  </button>
                ))}
                {masterClasses.length === 0 && (
                  <div className="text-gray-500 text-center p-4 text-sm">Нет уроков</div>
                )}
              </div>
            )}

            {/* Тренинги */}
            {contentType === CONTENT_TYPES.TRAINING && (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {trainings.map(item => (
                  <button
                    key={item.id}
                    onClick={() => addContentToBlock(blockIndex, CONTENT_TYPES.TRAINING, { 
                      id: item.id,
                      title: item.title,
                      path: `/trainings/${item.id}`
                    })}
                    className="w-full p-2 bg-gray-800 hover:bg-gray-700 rounded text-left transition-colors flex items-center gap-2 text-sm"
                  >
                    <GraduationCap className="w-4 h-4 text-orange-400 flex-shrink-0" />
                    <span className="text-white truncate">{item.title || 'Без названия'}</span>
                    <span className="text-gray-500 text-xs ml-auto">{item.lessons?.length || 0} уроков</span>
                  </button>
                ))}
                {trainings.length === 0 && (
                  <div className="text-gray-500 text-center p-4 text-sm">Нет тренингов</div>
                )}
              </div>
            )}

            {/* Кнопка */}
            {contentType === CONTENT_TYPES.BUTTON && (
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Текст кнопки"
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                  id="buttonText"
                  defaultValue="Перейти"
                />
                <input
                  type="url"
                  placeholder="URL ссылки"
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                  id="buttonUrl"
                />
                <select
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                  id="buttonTarget"
                >
                  <option value="_self">В этой же вкладке</option>
                  <option value="_blank">В новой вкладке</option>
                </select>
                <button
                  onClick={() => {
                    const text = document.getElementById('buttonText').value;
                    const url = document.getElementById('buttonUrl').value;
                    const target = document.getElementById('buttonTarget').value;
                    if (url) {
                      addContentToBlock(blockIndex, CONTENT_TYPES.BUTTON, { 
                        url, 
                        text: text || 'Перейти',
                        target 
                      });
                    } else {
                      alert('Введите URL ссылки');
                    }
                  }}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                >
                  Добавить кнопку
                </button>
              </div>
            )}

            {/* Текст */}
            {contentType === CONTENT_TYPES.TEXT && (
              <div className="space-y-3">
                <textarea
                  placeholder="Введите текст"
                  className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                  rows="4"
                  id="simpleText"
                />
                <button
                  onClick={() => {
                    const text = document.getElementById('simpleText').value;
                    if (text) {
                      addContentToBlock(blockIndex, CONTENT_TYPES.TEXT, { text });
                    }
                  }}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                >
                  Добавить текст
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Рендер элемента контента
  const renderContentItem = (content, blockIndex, contentIndex) => {
    return (
      <div key={content.id || contentIndex} className="bg-gray-800/50 rounded border border-gray-700 p-2">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-gray-600 cursor-move flex-shrink-0" />
          
          <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300 flex-shrink-0">
            {content.type === CONTENT_TYPES.VIDEO && '📹'}
            {(content.type === CONTENT_TYPES.AUDIO || content.type === CONTENT_TYPES.COURSE_AUDIO) && '🎵'}
            {content.type === CONTENT_TYPES.LESSON && '📚'}
            {content.type === CONTENT_TYPES.TRAINING && '🎓'}
            {content.type === CONTENT_TYPES.BUTTON && '🔗'}
            {content.type === CONTENT_TYPES.TEXT && '📝'}
          </span>
          
          <div className="flex-1 min-w-0">
            {content.type === CONTENT_TYPES.VIDEO && (
              <span className="text-xs text-gray-300 truncate block">ID: {content.data.kinescopeId}</span>
            )}
            {(content.type === CONTENT_TYPES.AUDIO || content.type === CONTENT_TYPES.COURSE_AUDIO) && (
              <div className="flex items-center gap-2">
                {content.data.image && (
                  <div className="w-6 h-6 rounded overflow-hidden flex-shrink-0">
                    <img src={content.data.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <span className="text-xs text-gray-300 truncate block">
                  {content.data.title || 'Аудио'}
                  {content.data.duration && ` (${content.data.duration})`}
                  {content.data.type === 'global' && <span className="text-gray-500 text-xs ml-1">📚</span>}
                  {content.data.type === 'course' && <span className="text-green-500 text-xs ml-1">📁</span>}
                </span>
              </div>
            )}
            {content.type === CONTENT_TYPES.LESSON && (
              <span className="text-xs text-gray-300 truncate block">{content.data.title || 'Урок'}</span>
            )}
            {content.type === CONTENT_TYPES.TRAINING && (
              <span className="text-xs text-gray-300 truncate block">{content.data.title || 'Тренинг'}</span>
            )}
            {content.type === CONTENT_TYPES.BUTTON && (
              <span className="text-xs text-gray-300 truncate block">{content.data.text}</span>
            )}
            {content.type === CONTENT_TYPES.TEXT && (
              <span className="text-xs text-gray-300 truncate block">
                {content.data.text?.substring(0, 30)}...
              </span>
            )}
          </div>
          
          <div className="flex gap-1 ml-auto flex-shrink-0">
            <button
              onClick={() => moveContentInBlock(blockIndex, contentIndex, 'up')}
              disabled={contentIndex === 0}
              className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-30"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button
              onClick={() => moveContentInBlock(blockIndex, contentIndex, 'down')}
              disabled={contentIndex === currentCourse.blocks[blockIndex].contents.length - 1}
              className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-30"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
            <button
              onClick={() => removeContentFromBlock(blockIndex, contentIndex)}
              className="p-1 bg-red-700/50 hover:bg-red-700 rounded text-white"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            Конструктор курсов
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => {
                resetGlobalAudioForm();
                setShowGlobalAudioManager(true);
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 text-sm"
            >
              <Library className="w-4 h-4" />
              Библиотека аудио
            </button>
            {currentCourse && (
              <button
                onClick={() => setShowCourseAudioUpload(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 text-sm"
              >
                <Upload className="w-4 h-4" />
                Аудио курса
              </button>
            )}
          </div>
        </div>

        {/* Список курсов */}
        <div className="bg-gray-900 rounded-lg p-4 mb-4 border border-gray-800">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-white">Курсы</h2>
            <button
              onClick={createNewCourse}
              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition flex items-center gap-1 text-sm"
            >
              <Plus className="w-4 h-4" />
              Создать
            </button>
          </div>

          <div className="space-y-1">
            {courses.map((course, index) => {
              const status = getCourseStatus(course);
              return (
                <div
                  key={course.id}
                  className={`flex items-center gap-2 p-2 border rounded transition text-sm ${
                    selectedCourseId === course.id 
                      ? 'border-blue-500 bg-blue-900/30' 
                      : 'border-gray-800 bg-gray-800/50 hover:bg-gray-800'
                  } ${course.status === 'draft' ? 'opacity-75' : ''}`}
                >
                  <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs">
                    {index + 1}
                  </div>

                  {course.image && (
                    <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0">
                      <img src={course.image} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}

                  <button
                    onClick={() => {
                      setSelectedCourseId(course.id);
                      setCurrentCourse(course);
                    }}
                    className="flex-1 text-left truncate"
                  >
                    <span className="font-medium text-white">{course.title}</span>
                    <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${status.bg} ${status.color}`}>
                      {status.icon}
                      {status.label}
                    </span>
                    {course.courseAudioLessons?.length > 0 && (
                      <span className="ml-2 text-xs text-green-400">
                        🎵 {course.courseAudioLessons.length}
                      </span>
                    )}
                  </button>

                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setCurrentCourse(course);
                        moveCourse('up');
                      }}
                      disabled={index === 0}
                      className="p-1 bg-gray-800 hover:bg-gray-700 rounded text-white disabled:opacity-30"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setCurrentCourse(course);
                        moveCourse('down');
                      }}
                      disabled={index === courses.length - 1}
                      className="p-1 bg-gray-800 hover:bg-gray-700 rounded text-white disabled:opacity-30"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}

            {courses.length === 0 && (
              <div className="text-center py-6 text-gray-500 border border-dashed border-gray-800 rounded text-sm">
                Нет курсов. Нажмите "Создать"
              </div>
            )}
          </div>
        </div>

        {/* Редактор курса */}
        {currentCourse && (
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-start">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={currentCourse.title}
                      onChange={(e) => setCurrentCourse({
                        ...currentCourse,
                        title: e.target.value
                      })}
                      className="text-xl font-bold bg-transparent border-b border-gray-700 focus:border-blue-500 outline-none text-white flex-1"
                      placeholder="Название курса"
                    />
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded ${getCourseStatus(currentCourse).bg} ${getCourseStatus(currentCourse).color}`}>
                        {getCourseStatus(currentCourse).icon}
                        {getCourseStatus(currentCourse).label}
                      </span>
                    </div>
                  </div>

                  {/* Изображение курса */}
                  <div className="flex items-center gap-3">
                    {currentCourse.image && (
                      <div className="w-16 h-16 rounded overflow-hidden border border-gray-700 flex-shrink-0">
                        <img 
                          src={currentCourse.image} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="course-image-upload"
                      />
                      
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="course-image-upload"
                          className={`inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700 text-sm ${
                            uploading ? 'opacity-50 pointer-events-none' : ''
                          }`}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          {uploading ? '...' : 'Загрузить'}
                        </label>
                        
                        {currentCourse.image && (
                          <button
                            onClick={() => setCurrentCourse({ ...currentCourse, image: '' })}
                            className="p-1.5 bg-red-600/20 text-red-400 rounded hover:bg-red-600/40"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Описание */}
                  <div>
                    <div className="flex flex-wrap gap-1 mb-2 p-1.5 bg-gray-800 rounded border border-gray-700">
                      <button
                        onClick={() => insertFormatting('bold')}
                        className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-white"
                        title="Жирный"
                      >
                        <Bold className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => insertFormatting('italic')}
                        className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-white"
                        title="Курсив"
                      >
                        <Italic className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => insertFormatting('underline')}
                        className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-white"
                        title="Подчеркнутый"
                      >
                        <Underline className="w-4 h-4" />
                      </button>
                      <div className="w-px h-6 bg-gray-700 mx-1"></div>
                      <button
                        onClick={() => insertFormatting('list')}
                        className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-white"
                        title="Список"
                      >
                        <List className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => insertFormatting('link')}
                        className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-white"
                        title="Ссылка"
                      >
                        <LinkIcon className="w-4 h-4" />
                      </button>
                    </div>

                    <textarea
                      id="course-description"
                      value={currentCourse.description || ''}
                      onChange={(e) => setCurrentCourse({
                        ...currentCourse,
                        description: e.target.value
                      })}
                      className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                      rows="3"
                      placeholder="Описание курса..."
                    />
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={toggleCourseStatus}
                    className={`p-2 rounded transition ${
                      currentCourse.status === 'draft'
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-600 text-white hover:bg-gray-700'
                    }`}
                    title={currentCourse.status === 'draft' ? 'Опубликовать курс' : 'Перевести в черновик'}
                  >
                    {currentCourse.status === 'draft' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={deleteCourse}
                    className="p-2 bg-red-600 text-white rounded hover:bg-red-700"
                    title="Удалить курс"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={saveCourse}
                    className="p-2 bg-green-600 text-white rounded hover:bg-green-700"
                    title="Сохранить курс"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Блоки курса */}
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-white">Блоки курса</h2>
                  <button
                    onClick={addBlock}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Добавить блок
                  </button>
                </div>

                {(!currentCourse.blocks || currentCourse.blocks.length === 0) ? (
                  <div className="text-center py-8 text-gray-500 border border-dashed border-gray-800 rounded-lg">
                    Нет блоков. Нажмите "Добавить блок"
                  </div>
                ) : (
                  <div className="space-y-4">
                    {currentCourse.blocks.map((block, blockIndex) => (
                      <div key={block.id} className="border border-gray-800 rounded-lg p-4 bg-gray-800/50">
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">Блок {blockIndex + 1}</span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => moveBlock(blockIndex, 'up')}
                                disabled={blockIndex === 0}
                                className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-30"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => moveBlock(blockIndex, 'down')}
                                disabled={blockIndex === currentCourse.blocks.length - 1}
                                className="p-1 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-30"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <button
                            onClick={() => removeBlock(blockIndex)}
                            className="text-red-400 hover:text-red-300 text-sm"
                            disabled={currentCourse.blocks.length <= 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="space-y-3">
                          {/* Название блока */}
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Название блока</label>
                            <input
                              type="text"
                              value={block.title}
                              onChange={(e) => updateBlock(blockIndex, 'title', e.target.value)}
                              className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                              placeholder="Название блока"
                            />
                          </div>

                          {/* Описание блока */}
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Описание блока</label>
                            <textarea
                              value={block.description}
                              onChange={(e) => updateBlock(blockIndex, 'description', e.target.value)}
                              className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                              rows="2"
                              placeholder="Описание блока"
                            />
                          </div>

                          {/* Изображение блока */}
                          <div>
                            <label className="text-xs text-gray-400 block mb-1">Изображение блока</label>
                            <div className="flex items-center gap-3">
                              {block.image && (
                                <div className="w-12 h-12 rounded overflow-hidden border border-gray-700 flex-shrink-0">
                                  <img 
                                    src={block.image} 
                                    alt="Preview" 
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <input
                                  type="file"
                                  ref={blockImageInputRef}
                                  accept="image/*"
                                  onChange={(e) => handleBlockImageUpload(e, blockIndex)}
                                  className="hidden"
                                  id={`block-image-upload-${blockIndex}`}
                                />
                                
                                <div className="flex items-center gap-2">
                                  <label
                                    htmlFor={`block-image-upload-${blockIndex}`}
                                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700 text-sm"
                                  >
                                    <Upload className="w-4 h-4 mr-1" />
                                    Загрузить
                                  </label>
                                  
                                  {block.image && (
                                    <button
                                      onClick={() => updateBlock(blockIndex, 'image', '')}
                                      className="p-1.5 bg-red-600/20 text-red-400 rounded hover:bg-red-600/40"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Контент блока */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-xs font-medium text-gray-400">
                                Контент ({block.contents?.length || 0})
                              </span>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => setShowContentSelector({ blockIndex, contentType: CONTENT_TYPES.VIDEO })}
                                  className="px-2 py-1 bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 rounded text-xs"
                                  title="Видео"
                                >
                                  <Video className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => setShowContentSelector({ blockIndex, contentType: CONTENT_TYPES.AUDIO })}
                                  className="px-2 py-1 bg-green-600/20 hover:bg-green-600/40 text-green-400 rounded text-xs"
                                  title="Аудио из библиотеки"
                                >
                                  <Library className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => setShowContentSelector({ blockIndex, contentType: CONTENT_TYPES.COURSE_AUDIO })}
                                  className="px-2 py-1 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded text-xs"
                                  title="Аудио курса"
                                >
                                  <Music className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => setShowContentSelector({ blockIndex, contentType: CONTENT_TYPES.LESSON })}
                                  className="px-2 py-1 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-400 rounded text-xs"
                                  title="Урок"
                                >
                                  <BookOpen className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => setShowContentSelector({ blockIndex, contentType: CONTENT_TYPES.TRAINING })}
                                  className="px-2 py-1 bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 rounded text-xs"
                                  title="Тренинг"
                                >
                                  <GraduationCap className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => setShowContentSelector({ blockIndex, contentType: CONTENT_TYPES.BUTTON })}
                                  className="px-2 py-1 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded text-xs"
                                  title="Кнопка"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => setShowContentSelector({ blockIndex, contentType: CONTENT_TYPES.TEXT })}
                                  className="px-2 py-1 bg-gray-600/20 hover:bg-gray-600/40 text-gray-400 rounded text-xs"
                                  title="Текст"
                                >
                                  📝
                                </button>
                              </div>
                            </div>

                            {block.contents && block.contents.length > 0 ? (
                              <div className="space-y-1">
                                {block.contents.map((content, contentIndex) => 
                                  renderContentItem(content, blockIndex, contentIndex)
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-3 text-gray-500 text-xs border border-dashed border-gray-700 rounded">
                                Нет контента
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Модальные окна */}
      {showContentSelector.blockIndex !== null && renderContentSelector(showContentSelector.blockIndex)}
      {renderGlobalAudioManager()}
      {renderCourseAudioManager()}
    </div>
  );
}